import { AutocompleteAdapter } from 'components/form/AutocompleteAdapter';
import ReactFinalForm from 'components/form/ReactFinalForm';
import { TextFieldAdapter } from 'components/form/TextFieldAdapter';
import { IOption, IStatusOption } from 'interfaces';
import { IdentityField } from 'components/form/IdentityField';
import { useTranslation } from 'react-i18next';
import {
  locationService,
  labelThresholdService,
  categoryService,
  productWarehouseService,
  stockQuantityService
} from 'services/api';
import toast from 'features/toast';
import { AxiosError } from 'axios';
import { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import { MultipleAutocompleteAdapter } from 'components/form/MultipleAutocompleteAdapter';
import { ProductWarehouseStatus } from '../../../constants';

interface MiscFormParams {
  // initial values
  row: any;
  open: boolean;
  onClose: () => void;
  // refresher functions
  refresh?: React.RefObject<() => void>;
}

function resolveProductWarehouseStatusOption(
  status: unknown,
  options: IStatusOption[]
): IStatusOption | undefined {
  if (status === null || status === undefined || status === '') {
    return undefined;
  }
  const str = String(status).trim();
  const num = Number(str);
  const byValue = options.find(
    (o) => o.value === status || o.value === num || String(o.value) === str
  );
  if (byValue) {
    return byValue;
  }
  return options.find(
    (o) => o.display_name === str || (typeof status === 'string' && o.display_name === status)
  );
}

function MiscDialogForm({ row, open, onClose, refresh }: MiscFormParams) {
  const { t } = useTranslation();
  const translationPrefix = 'pages.warehouse.edit.misc_product_warehouse_form';

  const [categories, setCategories] = useState<IOption[]>([]);
  const [labelCodes, setLabelCodes] = useState<string[]>([]);
  const [locations, setLocations] = useState<IOption[]>([]);

  const allStatusOptions: IStatusOption[] = useMemo(
    () =>
      Object.keys(ProductWarehouseStatus)
        .filter((key) => isNaN(Number(key)))
        .map((key) => ({
          value: ProductWarehouseStatus[key as keyof typeof ProductWarehouseStatus],
          display_name: key
        })),
    []
  );

  const rowStatusNum = useMemo(() => {
    return resolveProductWarehouseStatusOption(row.status, allStatusOptions)?.value;
  }, [row.status, allStatusOptions]);

  const stockQty = Number(row.total_stock_quantity ?? 0);

  const statusOptions = useMemo(
    () =>
      stockQty > 0
        ? allStatusOptions.filter(
            (o) => o.value !== ProductWarehouseStatus.DECOMMISSIONED || o.value === rowStatusNum
          )
        : allStatusOptions,
    [allStatusOptions, stockQty, rowStatusNum]
  );

  useEffect(() => {
    labelThresholdService.getAllBaseInformation().then((res) => {
      setLabelCodes(res.map((opt) => opt.code));
    });
    categoryService.getAllBaseInformation().then(setCategories);
    if (row.warehouse) {
      locationService.getAllBaseInformation({ warehouse: row.warehouse }).then(setLocations);
    } else {
      locationService.getAllBaseInformation().then(setLocations);
    }
  }, [row.warehouse]);

  const categoryCode = useMemo(
    () => (row.category != null && row.category !== '' ? String(row.category).trim() : ''),
    [row.category]
  );

  const categoryInit = useMemo(
    () =>
      categoryCode ? categories.find((c) => String(c.code).trim() === categoryCode) : undefined,
    [categories, categoryCode]
  );

  const statusInit = useMemo(
    () => resolveProductWarehouseStatusOption(row.status, allStatusOptions),
    [row.status, allStatusOptions]
  );

  const labelCode = row.label != null && row.label !== '' ? String(row.label).trim() : '';

  const labelOptions = useMemo(() => {
    const opts = labelCodes.map((code) => ({ code, display_name: code }));
    if (labelCode && !labelCodes.includes(labelCode)) {
      return [{ code: labelCode, display_name: labelCode }, ...opts];
    }
    return opts;
  }, [labelCodes, labelCode]);

  const labelInit = useMemo(() => {
    if (!labelCode) {
      return null;
    }
    return { code: labelCode, display_name: labelCode };
  }, [labelCode]);

  const locationCodes = useMemo(() => {
    const loc = row.location;
    if (Array.isArray(loc)) {
      return loc.map((c: string) => String(c).trim()).filter(Boolean);
    }
    if (loc != null && loc !== '') {
      return [String(loc).trim()].filter(Boolean);
    }
    return [];
  }, [row.location]);

  const locationInitValue = useMemo(
    () =>
      locationCodes
        .map((code) => locations.find((s) => String(s.code).trim() === code))
        .filter((x): x is IOption => Boolean(x)),
    [locationCodes, locations]
  );

  const decommissionBlockedMessage = t(`${translationPrefix}.cannot-decommission-with-stock`);

  const handleSubmit = async (values: any) => {
    const newStatus = values.status;

    if (newStatus === ProductWarehouseStatus.DECOMMISSIONED) {
      try {
        const stockRes = await stockQuantityService.getStockQuantityWithLocation(1000, 0, {
          product: row.product,
          warehouse: row.warehouse
        });
        const totalStock = (stockRes.results || []).reduce(
          (sum: number, item: { quantity?: number }) => sum + (item.quantity || 0),
          0
        );
        if (totalStock > 0) {
          toast.error(decommissionBlockedMessage);
          return;
        }
      } catch {
        // Fall through to backend validation if stock lookup fails.
      }
    }

    const updatedValues = {
      ...values,
      product: row.product,
      warehouse: row.warehouse,
      label:
        values.label === '' || values.label === undefined || values.label === null
          ? null
          : values.label,
      category:
        values.category === '' || values.category === undefined || values.category === null
          ? null
          : values.category
    };

    try {
      await productWarehouseService.updateThresholds([updatedValues]);
      if (refresh && refresh.current) {
        refresh.current();
        toast.success(t('actions.successfully.updated'));
      }
      onClose();
    } catch (error: unknown) {
      if (
        newStatus === ProductWarehouseStatus.DECOMMISSIONED &&
        error instanceof AxiosError &&
        error.response?.status === 400
      ) {
        toast.error(decommissionBlockedMessage);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t('global.edit')}</DialogTitle>
      <DialogContent>
        <div>
          <Typography variant="h6" gutterBottom>
            {t(`${translationPrefix}.edit-threshold-title`, {
              product: row.product,
              warehouse: row.warehouse
            })}
          </Typography>
          <ReactFinalForm
            key={`${row.product}-${row.warehouse}-${open}-${categories.length}-${
              labelCodes.length
            }-${locations.length}-${locationCodes.join(',')}`}
            onSubmit={handleSubmit}
            hideControls={true}
            formId="updateProductWarehouseThresholdsAndLocationForm">
            <h3 className="text-lg font-semibold">
              {t(`${translationPrefix}.thresholds-edit-section`)}
            </h3>
            <div className="grid gap-2 p-2">
              <div className="grid grid-cols-2 gap-2">
                <IdentityField
                  name="category"
                  label={t('global.category')}
                  component={AutocompleteAdapter}
                  initValue={categoryInit}
                  options={categories}
                  optionValue="code"
                  placeholder="Category"
                  autoSelect={false}
                />

                {statusOptions && (
                  <IdentityField
                    name="status"
                    label={t(`entities.product.status`)}
                    component={AutocompleteAdapter}
                    initValue={statusInit}
                    options={statusOptions}
                    placeholder="Status"
                    type="text"
                    optionLabel="display_name"
                    optionValue="value"
                    autoSelect={false}
                  />
                )}
              </div>
              <div className=" grid grid-cols-4 gap-2">
                <div className="">
                  <IdentityField
                    name="label"
                    component={AutocompleteAdapter}
                    options={labelOptions}
                    initValue={labelInit}
                    label={t('global.label')}
                    optionValue="code"
                    optionLabel="display_name"
                    freeSolo={true}
                    autoSelect={false}
                  />
                </div>
                <div className="">
                  <IdentityField
                    name="understock_threshold"
                    component={TextFieldAdapter}
                    initialValue={row.understock_threshold}
                    label={t('entities.product.understock_threshold')}
                    type="number"
                  />
                </div>
                <div className="">
                  <IdentityField
                    name="overstock_threshold"
                    component={TextFieldAdapter}
                    initialValue={row.overstock_threshold}
                    label={t('entities.product.overstock_threshold')}
                    type="number"
                  />
                </div>
                <div className="">
                  <IdentityField
                    name="restock_threshold"
                    component={TextFieldAdapter}
                    label={t('entities.product.restock_threshold')}
                    initialValue={row.restock_threshold}
                    type="number"
                  />
                </div>
              </div>
            </div>
            <div className="w-full gap-2 p-2">
              <IdentityField
                name="notes"
                component={TextFieldAdapter}
                initialValue={row.notes}
                label={t('global.notes')}
                type="text"
              />
            </div>
            <br />
            {locations.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold">
                  {t(`${translationPrefix}.locations-edit-section`)}
                </h3>
                <div className="w-full mb-4 p-2">
                  <IdentityField
                    name="locations"
                    component={MultipleAutocompleteAdapter}
                    options={locations}
                    initValue={locationInitValue}
                    label={t('global.locations')}
                    type="text"
                  />
                </div>
              </div>
            )}
          </ReactFinalForm>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.cancel')}</Button>
        <Button
          type="submit"
          form="updateProductWarehouseThresholdsAndLocationForm"
          variant="outlined">
          {t('actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MiscDialogForm;
